import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession, isSuperAdmin } from '../../adminSession';
import '../../Admin.scss';

type Contract = {
  id: number;
  authChurch?: string;
  customer_name: string;
  phone: string;
  contract_type: string;
  contract_name: string | null;
  product_name: string | null;
  company_name: string | null;
  representative: string | null;
  email: string | null;
  created_at: string;
  signed_at: string | null;
};

type ApiEnvelope<T> = {
  ok: boolean;
  data?: T;
  message?: string;
};

const CONTRACT_TYPE_LABEL: Record<string, string> = {
  web: '웹 제작',
  app: '앱 제작',
  maintenance: '유지보수',
  consulting: '컨설팅',
  manual: '직접등록',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd}`;
}

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

type CreateChurchForm = {
  authChurch: string;
  churchName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  userAccount: string;
  password: string;
  // '' = 신규 교회 기본 area(area 관리에서 지정)
  area: string;
};

type AreaOption = { area: number; name: string; isDefault: boolean };

const EMPTY_CREATE_FORM: CreateChurchForm = {
  authChurch: '',
  churchName: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  userAccount: '',
  password: '',
  area: '',
};

type EditForm = {
  company_name: string;
  representative: string;
  phone: string;
  email: string;
};

function editFormOf(item: Contract): EditForm {
  return {
    company_name: item.company_name || item.contract_name || '',
    representative: item.representative || item.customer_name || '',
    phone: item.phone || '',
    email: item.email || '',
  };
}

export default function ChurchList() {
  const session = getAdminSession();
  const canRegister = isSuperAdmin(session);
  const [items, setItems] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [q, setQ] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateChurchForm>(EMPTY_CREATE_FORM);
  const [areaOptions, setAreaOptions] = useState<AreaOption[]>([]);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');

  const [editItem, setEditItem] = useState<Contract | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ company_name: '', representative: '', phone: '', email: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  const [accountLoading, setAccountLoading] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [userAccount, setUserAccount] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<{ authChurch: string; churchName: string } | null>(null);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const fetchContracts = useCallback(async (query: string) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<Contract[]>>(`${MainURL}/api/admin/contracts/list`, {
        params: {
          status: 'completed',
          ...(query ? { q: query } : {}),
        },
      });
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
      } else {
        setErrorMsg(res.data?.message || '교회(계약) 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      const data = axios.isAxiosError(err) ? err.response?.data : null;
      const msg =
        data && typeof data === 'object' && 'message' in data
          ? String((data as { message?: string }).message)
          : null;
      setErrorMsg(msg || '교회(계약) 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchContracts(q);
  };

  const openCreateModal = () => {
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateError('');
    setCreateOpen(true);
    if (session) {
      axios
        .get<ApiEnvelope<AreaOption[]>>(`${MainURL}/admin-console/areas`, { params: { requesterId: session.id } })
        .then((res) => {
          if (res.data?.ok && res.data.data) setAreaOptions(res.data.data);
        })
        .catch(() => setAreaOptions([]));
    }
  };

  const closeCreateModal = () => setCreateOpen(false);

  const submitCreate = async () => {
    if (!session) return;
    if (!createForm.authChurch.trim()) {
      setCreateError('authChurch(번들 ID)를 입력해 주세요.');
      return;
    }
    if (!createForm.churchName.trim()) {
      setCreateError('교회명을 입력해 주세요.');
      return;
    }
    if (!createForm.userAccount.trim() || !createForm.password) {
      setCreateError('로그인 아이디와 비밀번호를 입력해 주세요.');
      return;
    }
    if (!isValidEmail(createForm.userAccount)) {
      setCreateError('로그인 아이디는 이메일 형식으로 입력해 주세요.');
      return;
    }
    setCreateSubmitting(true);
    setCreateError('');
    try {
      const res = await axios.post<ApiEnvelope<{ authChurch: string }>>(`${MainURL}/admin-console/church-apps`, {
        requesterId: session.id,
        ...createForm,
        area: createForm.area ? Number(createForm.area) : undefined,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      closeCreateModal();
      alert(`${createForm.churchName} 교회가 등록되었습니다 (authChurch: ${res.data.data?.authChurch}).`);
      fetchContracts(q);
    } catch (err) {
      setCreateError(errorMessageOf(err, '교회 등록에 실패했습니다.'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openEditModal = (item: Contract) => {
    setEditItem(item);
    setEditForm(editFormOf(item));
    setEditError('');
    setUserAccount('');
    setNewPassword('');
    setAccountExists(false);

    if (session && item.authChurch) {
      setAccountLoading(true);
      axios
        .get<ApiEnvelope<{ exists: boolean; userAccount: string | null }>>(
          `${MainURL}/admin-console/church-apps/${encodeURIComponent(item.authChurch)}/admin-account`,
          { params: { requesterId: session.id } },
        )
        .then((res) => {
          if (res.data?.ok && res.data.data) {
            setAccountExists(res.data.data.exists);
            setUserAccount(res.data.data.userAccount || '');
          }
        })
        .catch(() => {
          // 계정 정보 조회 실패는 무시 - 아이디/비밀번호 칸을 빈 채로 두면 그대로 신규 생성 시도로 이어짐
        })
        .finally(() => setAccountLoading(false));
    }
  };

  const closeEditModal = () => setEditItem(null);

  // id > 0인 항목은 client.online_contracts(결제 계약), id < 0인 항목은 계약 없이 직접등록된
  // churchAdmin.churches(getContractList가 합성해서 내려준 항목, authChurch 필수) - 두 데이터 소스가
  // 서로 다른 API로 수정된다. 로그인 계정(아이디/비밀번호)은 두 유형 모두 authChurch 기준으로 같은
  // churchAdmin.admins(super)를 upsert - 결제 발급 건은 계정이 아직 없을 수 있어(churchProvisioning.js
  // 참고) 최초 생성으로 처리된다.
  const submitEdit = async () => {
    if (!session || !editItem) return;
    if (!editForm.company_name.trim()) {
      setEditError('교회(회사)명을 입력해 주세요.');
      return;
    }
    if (!editForm.phone.trim()) {
      setEditError('연락처를 입력해 주세요.');
      return;
    }
    if (editItem.authChurch && userAccount.trim() && !accountExists && !newPassword) {
      setEditError('아직 로그인 계정이 없습니다 - 최초 생성 시 비밀번호를 입력해 주세요.');
      return;
    }
    if (editItem.authChurch && userAccount.trim() && !isValidEmail(userAccount)) {
      setEditError('로그인 아이디는 이메일 형식으로 입력해 주세요.');
      return;
    }
    setEditSubmitting(true);
    setEditError('');
    try {
      if (editItem.id < 0) {
        if (!editItem.authChurch) throw new Error('authChurch 정보를 찾을 수 없습니다.');
        const res = await axios.put<ApiEnvelope<null>>(
          `${MainURL}/admin-console/church-apps/${encodeURIComponent(editItem.authChurch)}/contact`,
          {
            requesterId: session.id,
            churchName: editForm.company_name.trim(),
            contactName: editForm.representative.trim(),
            contactEmail: editForm.email.trim(),
            contactPhone: editForm.phone.trim(),
          },
        );
        if (!res.data?.ok) throw new Error(res.data?.message);
      } else {
        const res = await axios.patch<ApiEnvelope<null>>(`${MainURL}/api/admin/contracts/${editItem.id}/contact`, {
          company_name: editForm.company_name.trim(),
          representative: editForm.representative.trim(),
          phone: editForm.phone.trim(),
          email: editForm.email.trim(),
        });
        if (!res.data?.ok) throw new Error(res.data?.message);
      }

      if (editItem.authChurch && userAccount.trim()) {
        const res = await axios.put<ApiEnvelope<null>>(
          `${MainURL}/admin-console/church-apps/${encodeURIComponent(editItem.authChurch)}/admin-account`,
          { requesterId: session.id, userAccount: userAccount.trim(), password: newPassword },
        );
        if (!res.data?.ok) throw new Error(res.data?.message);
      }

      closeEditModal();
      fetchContracts(q);
    } catch (err) {
      setEditError(errorMessageOf(err, '수정하지 못했습니다.'));
    } finally {
      setEditSubmitting(false);
    }
  };

  // 앱관리의 교회 삭제와 같은 API - 결제 계약(client.online_contracts)까지 authChurch 기준으로 함께 지워진다
  // (churchLifecycle.js deleteAuthChurch). authChurch가 없는 계약 행은 삭제 대상이 아님.
  const openDeleteModal = (item: Contract) => {
    if (!item.authChurch) return;
    setDeleteTarget({ authChurch: item.authChurch, churchName: item.company_name || item.contract_name || item.customer_name });
    setDeleteTyped('');
  };

  const closeDeleteModal = () => setDeleteTarget(null);

  const handleDelete = async () => {
    if (!session || !deleteTarget) return;
    const { authChurch } = deleteTarget;
    if (deleteTyped !== authChurch) return;
    closeDeleteModal();
    setDeletingKey(authChurch);
    try {
      const res = await axios.delete<ApiEnvelope<null>>(
        `${MainURL}/admin-console/church-apps/${encodeURIComponent(authChurch)}`,
        { data: { requesterId: session.id } },
      );
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchContracts(q);
    } catch (err) {
      alert(errorMessageOf(err, '삭제에 실패했습니다.'));
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">등록 교회 목록</h2>
          <p className="admin-panel-desc">
            최종결제까지 완료되었거나 최종관리자가 직접 등록한, 실제로 등록된 교회 현황입니다. 담당자, 연락처,
            계약 형태(직접등록 포함)를 확인할 수 있습니다. 진행 중인 신청 상태는 &apos;신청 목록&apos; 메뉴에서
            확인하세요.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canRegister && (
            <button type="button" className="admin-refresh-btn" onClick={openCreateModal}>
              + 교회 직접등록
            </button>
          )}
          <button type="button" className="admin-refresh-btn" onClick={() => fetchContracts(q)} disabled={loading}>
            {loading ? '불러오는 중...' : '새로고침'}
          </button>
        </div>
      </div>

      <form className="admin-filterbar" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="교회명 · 담당자 · 연락처 검색"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit" className="admin-refresh-btn">검색</button>
      </form>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>최종결제일</th>
              <th>교회(회사)명</th>
              <th>담당자</th>
              <th>연락처</th>
              <th>이메일</th>
              <th>계약 형태</th>
              {canRegister && <th>관리</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={canRegister ? 7 : 6}>등록된 교회가 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.authChurch ?? item.id}>
                  <td>{formatDateTime(item.created_at)}</td>
                  <td>{item.company_name || item.contract_name || '-'}</td>
                  <td>{item.representative || item.customer_name}</td>
                  <td>{item.phone}</td>
                  <td>{item.email || '-'}</td>
                  <td>{CONTRACT_TYPE_LABEL[item.contract_type] ?? item.contract_type}</td>
                  {canRegister && (
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="admin-btn edit-btn" onClick={() => openEditModal(item)}>
                        수정
                      </button>
                      {item.authChurch && (
                        <button
                          type="button"
                          className="admin-btn delete-btn"
                          onClick={() => openDeleteModal(item)}
                          disabled={deletingKey === item.authChurch}
                        >
                          {deletingKey === item.authChurch ? '삭제 중...' : '삭제'}
                        </button>
                      )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>교회 직접등록</h3>
              <button type="button" onClick={closeCreateModal}>×</button>
            </div>
            <div className="modal-body">
              <p className="admin-panel-desc" style={{ marginBottom: 20 }}>
                온라인 결제 없이 최종관리자가 직접 교회를 등록합니다. 여기서 입력한 로그인 아이디(이메일) ·
                비밀번호로 churchAdmin.admins에 최종관리자(super) 계정이 그대로 생성됩니다(자동 발급
                없음). 결제 계약이 없는 건이라 계약 형태에 &quot;직접등록&quot;으로 표시되며, 위
                목록과 &quot;앱관리&quot; 메뉴 양쪽에서 확인할 수 있습니다.
              </p>
              <div className="form-group">
                <label>authChurch(교회앱 ID) * — 사용자앱 번들 ID를 그대로 사용하는 것을 권장 (예: com.newchurch)</label>
                <input
                  type="text"
                  value={createForm.authChurch}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, authChurch: e.target.value }))}
                  placeholder="com.newchurch"
                />
              </div>
              <div className="form-group">
                <label>area — 이 교회 데이터가 저장될 곳. 등록 후에는 바꿀 수 없고, 이 교회 앱들의 APP_AREA도 같은 값으로 빌드해야 합니다.</label>
                <select
                  value={createForm.area}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, area: e.target.value }))}
                >
                  <option value="">
                    기본 area
                    {areaOptions.find((a) => a.isDefault) ? ` (area ${areaOptions.find((a) => a.isDefault)?.area})` : ''}
                  </option>
                  {areaOptions.map((a) => (
                    <option key={a.area} value={String(a.area)}>
                      area {a.area}
                      {a.name && a.name !== `area ${a.area}` ? ` · ${a.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>교회명 *</label>
                <input
                  type="text"
                  value={createForm.churchName}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, churchName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>로그인 아이디 (이메일) *</label>
                <input
                  type="email"
                  value={createForm.userAccount}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, userAccount: e.target.value }))}
                  placeholder="example@email.com"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label>비밀번호 *</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
              <p className="admin-subtext" style={{ margin: '0 0 12px' }}>
                위 로그인 계정이 모든 앱 · 웹 관리 페이지의 교회 관리자 계정이 됩니다. 앱(사용자앱 포함)은 등록 후 앱관리에서 &quot;사용 시작&quot;을 눌러야 켜집니다.
              </p>
              <div className="form-group">
                <label>담당자명</label>
                <input
                  type="text"
                  value={createForm.contactName}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, contactName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>이메일</label>
                <input
                  type="email"
                  value={createForm.contactEmail}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>연락처</label>
                <input
                  type="text"
                  value={createForm.contactPhone}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, contactPhone: e.target.value }))}
                />
              </div>
              {createError && <p className="admin-error-text">{createError}</p>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeCreateModal}>
                취소
              </button>
              <button type="button" className="btn btn-primary" onClick={submitCreate} disabled={createSubmitting}>
                {createSubmitting ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editItem && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>교회 정보 수정</h3>
              <button type="button" onClick={closeEditModal}>×</button>
            </div>
            <div className="modal-body">
              <p className="admin-panel-desc" style={{ marginBottom: 20 }}>
                교회(회사)명 · 담당자 · 연락처 · 이메일 · 로그인 아이디/비밀번호를 수정합니다. 계약 금액/기간
                등 계약 조건은 &apos;신청 목록&apos; 메뉴에서, authChurch 문자열 자체는 &apos;앱관리&apos;
                메뉴의 &quot;ID 변경&quot;에서 수정하세요.
              </p>
              <div className="form-group">
                <label>교회(회사)명 *</label>
                <input
                  type="text"
                  value={editForm.company_name}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, company_name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>담당자</label>
                <input
                  type="text"
                  value={editForm.representative}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, representative: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>연락처 *</label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>이메일</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </div>

              <hr style={{ margin: '20px 0' }} />
              <h4 style={{ margin: '0 0 8px' }}>로그인 계정 (churchAdmin.admins, 최종관리자)</h4>
              {!editItem.authChurch ? (
                <p className="admin-panel-desc">authChurch가 아직 발급되지 않아 로그인 계정을 관리할 수 없습니다.</p>
              ) : (
                <>
                  <p className="admin-panel-desc" style={{ marginBottom: 12 }}>
                    {accountLoading
                      ? '계정 정보를 불러오는 중...'
                      : accountExists
                        ? '아이디를 바꾸거나, 비밀번호를 바꾸려면 새 비밀번호를 입력하세요(비워두면 비밀번호는 유지됩니다).'
                        : '이 교회는 아직 로그인 계정이 없습니다 - 아이디/비밀번호를 입력하면 최종관리자 계정이 새로 생성됩니다.'}
                  </p>
                  <div className="form-group">
                    <label>로그인 아이디 (이메일){!accountExists && ' *'}</label>
                    <input
                      type="email"
                      value={userAccount}
                      onChange={(e) => setUserAccount(e.target.value)}
                      placeholder="example@email.com"
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group">
                    <label>{accountExists ? '새 비밀번호 (변경 시에만 입력)' : '비밀번호 *'}</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <p className="admin-subtext" style={{ margin: '4px 0 0' }}>
                      결제로 등록된 교회는 로그인 정보를 고객에게 전달하기 전이면 여기서 정한 비밀번호가 전달할 임시
                      비밀번호가 됩니다(앱관리 &gt; 수정에서 확인·전달).
                    </p>
                  </div>
                </>
              )}

              {editError && <p className="admin-error-text">{editError}</p>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
                취소
              </button>
              <button type="button" className="btn btn-primary" onClick={submitEdit} disabled={editSubmitting}>
                {editSubmitting ? '수정 중...' : '수정'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>교회 삭제</h3>
              <button type="button" onClick={closeDeleteModal}>×</button>
            </div>
            <div className="modal-body">
              <p className="admin-panel-desc" style={{ marginBottom: 12, userSelect: 'text' }}>
                {deleteTarget.churchName}({deleteTarget.authChurch}) 교회를 삭제하면 계약·결제 기록과 예배/설교/소식/교적/출석/재정/주일학교
                등 이 교회의 모든 데이터가 영구 삭제되며 되돌릴 수 없습니다.
              </p>
              <p style={{ fontSize: 14, userSelect: 'text' }}>
                계속하려면 authChurch 값을 정확히 입력하세요: <strong style={{ userSelect: 'all' }}>{deleteTarget.authChurch}</strong>
              </p>
              <div className="form-group">
                <input
                  type="text"
                  value={deleteTyped}
                  onChange={(e) => setDeleteTyped(e.target.value)}
                  placeholder={deleteTarget.authChurch}
                  autoComplete="off"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleDelete();
                  }}
                />
              </div>
              {deleteTyped !== '' && deleteTyped !== deleteTarget.authChurch && (
                <p className="admin-error-text">입력한 authChurch가 일치하지 않습니다.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeDeleteModal}>
                취소
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleteTyped !== deleteTarget.authChurch}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
